import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { Page } from 'module/ui/components/molecules/page';
import { Argon } from 'module/ui/components/templates/argon';
import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import {
  useUpdatePeoplePageTemplateMutation,
  useCreatePeoplePageTemplateMutation,
} from 'queries/people-page-template';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
import { DocumentTitle } from 'components/molecules/document-title';
import {
  CurrentDocumentEditorProvider,
  useCurrentDocumentEditor,
} from 'components/organisms/company-page-template/editor-context';
import { useFields } from 'components/organisms/people-page-template/manage/fields';
import { ManagePeoplePageTemplate } from 'components/organisms/people-page-template/manage/manage-page-template';
import { useManageTemplateState } from 'components/organisms/people-page-template/manage/template-state';
import { PeoplePageTemplateState } from 'components/organisms/people-page-template/manage/types';
import {
  stateToCreateApiPayload,
  stateToUpdateApiPayload,
} from 'components/organisms/people-page-template/utils/state-to-api';

const {
  Header: { Title },
} = Page;
const BasePeoplePageTemplateManagePage = () => {
  const { fields, lookups, isLoading: isFieldsLoading } = useFields();
  const navigate = useNavigate();
  const {
    isLoading,
    templateState,
    setTemplateState,
    originalTemplate,
    isEdit,
  } = useManageTemplateState({
    fieldsLookup: lookups,
    isFieldsLoading,
  });
  const { editor } = useCurrentDocumentEditor();
  const updateTemplateState = useCallback(
    async (next: Partial<PeoplePageTemplateState>) => {
      if (templateState == null) {
        return;
      }

      setTemplateState(next);
    },
    [setTemplateState, templateState],
  );
  const {
    mutateAsync: updatePeoplePageTemplate,
    isPending: isTemplateUpdating,
  } = useUpdatePeoplePageTemplateMutation();
  const {
    mutateAsync: createPeoplePageTemplate,
    isPending: isTemplateCreating,
  } = useCreatePeoplePageTemplateMutation();
  const saveTemplate = useCallback(async () => {
    if (templateState == null || editor == null) {
      return;
    }
    if (isEdit) {
      if (originalTemplate == null) {
        return;
      }
      await updatePeoplePageTemplate(
        stateToUpdateApiPayload(
          templateState,
          editor,
          originalTemplate,
          lookups,
        ),
      );
    } else {
      await createPeoplePageTemplate(
        stateToCreateApiPayload(templateState, editor, lookups),
      ).then((res) => {
        if (res != null) {
          navigate(
            `/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.PEOPLE_PAGE_TEMPLATE_LIST}/${ROUTE_PATHS.PEOPLE_PAGE_TEMPLATE_EDIT.replace(':peoplePageTemplateId', res.templateId.toString())}`,
          );
        }
      });
    }
  }, [
    createPeoplePageTemplate,
    editor,
    isEdit,
    lookups,
    navigate,
    originalTemplate,
    templateState,
    updatePeoplePageTemplate,
  ]);

  return isLoading || isFieldsLoading || templateState == null ? (
    <PageLoading />
  ) : (
    <Argon
      breadcrumbs={
        <>
          <Breadcrumb.Item>
            <Link
              to={`/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.PEOPLE_PAGE_TEMPLATE_LIST}`}
            >
              <FormattedMessage id="people_page_templates.title" />
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>&nbsp;</Breadcrumb.Item>
        </>
      }
      title={
        <DocumentTitle
          value={templateState.name}
          onChange={(value) => {
            updateTemplateState({ name: value });
          }}
          renderTitle={(value) => <Title>{value}</Title>}
          isDisabled={originalTemplate?.isDefault ?? false}
        />
      }
      content={
        <ManagePeoplePageTemplate
          template={templateState}
          onChange={updateTemplateState}
          fields={fields}
          fieldsLookup={lookups}
        />
      }
      actions={{}}
      pageActions={
        <Button
          variant="primary"
          rightIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
          loading={isTemplateUpdating || isTemplateCreating}
          onClick={saveTemplate}
        >
          <FormattedMessage id="save" />
        </Button>
      }
    />
  );
};

export const PeoplePageTemplateManagePage = () => {
  return (
    <CurrentDocumentEditorProvider>
      <BasePeoplePageTemplateManagePage />
    </CurrentDocumentEditorProvider>
  );
};
